import { useState, useEffect, useCallback } from 'react';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiry: number;
}

interface UseCacheOptions {
  ttl?: number; // Time to live in milliseconds
  staleWhileRevalidate?: boolean;
}

const cache = new Map<string, CacheEntry<any>>();

export function useCache<T>(
  key: string,
  fetcher: () => Promise<T>,
  options: UseCacheOptions = {}
) {
  const { ttl = 5 * 60 * 1000, staleWhileRevalidate = true } = options; // Default 5 minutes

  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const isExpired = useCallback((entry: CacheEntry<T>) => {
    return Date.now() > entry.expiry;
  }, []);

  const fetchData = useCallback(async (useStale = false) => {
    const cachedEntry = cache.get(key) as CacheEntry<T> | undefined;

    // Return stale data immediately if available and requested
    if (useStale && cachedEntry && !loading) {
      setData(cachedEntry.data);
      setError(null);
    }

    // Check if we have fresh data
    if (cachedEntry && !isExpired(cachedEntry)) {
      setData(cachedEntry.data);
      setError(null);
      return cachedEntry.data;
    }

    // Fetch fresh data
    setLoading(true);
    setError(null);

    try {
      const freshData = await fetcher();

      // Cache the fresh data
      cache.set(key, {
        data: freshData,
        timestamp: Date.now(),
        expiry: Date.now() + ttl
      });

      setData(freshData);
      return freshData;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error');
      setError(error);

      // If we have stale data and staleWhileRevalidate is enabled, use it
      if (staleWhileRevalidate && cachedEntry) {
        setData(cachedEntry.data);
        return cachedEntry.data;
      }

      throw error;
    } finally {
      setLoading(false);
    }
  }, [key, fetcher, ttl, isExpired, loading, staleWhileRevalidate]);

  const invalidate = useCallback(() => {
    cache.delete(key);
  }, [key]);

  const refresh = useCallback(() => {
    invalidate();
    return fetchData();
  }, [invalidate, fetchData]);

  useEffect(() => {
    const cachedEntry = cache.get(key) as CacheEntry<T> | undefined;

    if (cachedEntry && !isExpired(cachedEntry)) {
      // Use cached data
      setData(cachedEntry.data);
      setError(null);
    } else if (staleWhileRevalidate && cachedEntry) {
      // Use stale data while revalidating
      fetchData(true);
    } else {
      // Fetch fresh data
      fetchData();
    }
  }, [key]); // Only depend on key to avoid infinite loops

  return {
    data,
    loading,
    error,
    refresh,
    invalidate,
    refetch: fetchData
  };
}

// Utility function to clear all cache
export const clearCache = () => {
  cache.clear();
};

// Utility function to clear specific cache entries by pattern
export const clearCacheByPattern = (pattern: RegExp) => {
  const keys = Array.from(cache.keys());
  keys.forEach(key => {
    if (pattern.test(key)) {
      cache.delete(key);
    }
  });
};